/*
 * The daily puzzle's own seeded random numbers (ADR 0127 §4).
 *
 * Not `withSeed`/`getRandomInt`: the `random` package is mapped to a different
 * algorithm in Jest, so a test could not pin what a date produces in the
 * browser, and a dependency update or a change to a shared generator's calls
 * would silently change every past day's board. This is a hashed string seed
 * (FNV-1a, 32 bit) driving mulberry32, both a few lines of integer arithmetic
 * that mean the same thing in every JavaScript engine.
 *
 * Do not change either function. A different sequence is a different board for
 * a date already played; a deliberate change to the generator goes in as a new
 * version in `GENERATOR_VERSIONS` (equalGroups.ts), applying from its start date.
 */

/** FNV-1a, 32 bit, over the UTF-16 code units of the string. */
export function hashString(text: string): number {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}

/** mulberry32: a uniform float in [0, 1) per call. */
function mulberry32(seed: number): () => number {
    let state = seed >>> 0;
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export interface Rng {
    /** Uniform float in [0, 1). */
    next(): number;
    /** Uniform integer in [min, max], both included. */
    int(min: number, max: number): number;
    pick<T>(items: readonly T[]): T;
    /** A new array in random order (Fisher-Yates); the input is left alone. */
    shuffle<T>(items: readonly T[]): T[];
    /** One item, chosen in proportion to its integer weight (all weights >= 1). */
    weighted<T>(items: readonly T[], weightOf: (item: T) => number): T;
}

export function createRng(seed: string): Rng {
    const next = mulberry32(hashString(seed));
    const int = (min: number, max: number) => min + Math.floor(next() * (max - min + 1));
    return {
        next,
        int,
        pick: (items) => items[int(0, items.length - 1)],
        shuffle: (items) => {
            const copy = [...items];
            for (let i = copy.length - 1; i > 0; i--) {
                const j = int(0, i);
                [copy[i], copy[j]] = [copy[j], copy[i]];
            }
            return copy;
        },
        weighted: (items, weightOf) => {
            const weights = items.map(weightOf);
            const total = weights.reduce((sum, weight) => sum + weight, 0);
            let point = int(0, total - 1);
            for (let i = 0; i < items.length; i++) {
                if (point < weights[i]) {
                    return items[i];
                }
                point -= weights[i];
            }
            return items[items.length - 1];
        },
    };
}
